import { Extension } from "@codemirror/state";
import { EditorView, ViewPlugin, ViewUpdate } from "@codemirror/view";
import createDomElement from "utils/createDomElement";

import i18n from "i18n/config";
import { isMac } from "services/browser";

import CopilotIcon from "assets/icons/Copilot.svg?raw";

import {
  aiAssistantStateField,
  setAiAssistantState,
} from "../aiAssistantWidget/aiAssistantWidget";
import { StatementGutterMarkerState } from "../statementGutter/statementGutterMarker";

import styles from "./aiIconWidget.module.scss";

const WIDGET_WIDTH = 24;
const WIDGET_MARGIN = 4;
const GUTTER_WIDTH = 50;
const EDITOR_RIGHT_PADDING = 12;

type Placement = {
  targetLeft: number;
  targetTop: number;
  visible: boolean;
  title: string;
  tooltipRight: boolean;
};

const normalizeSelectionAnchorPlacement = (placement: Placement) => {
  const thresholdHorizontal = GUTTER_WIDTH;
  const thresholdVertical = 0 + WIDGET_MARGIN;
  const { targetLeft, targetTop } = placement;

  if (targetTop - thresholdVertical < 0) {
    return {
      ...placement,
      targetLeft: targetLeft + WIDGET_WIDTH + WIDGET_MARGIN,
      targetTop: thresholdVertical,
    };
  }

  if (targetLeft - thresholdHorizontal < 0) {
    return {
      ...placement,
      targetLeft: thresholdHorizontal,
    };
  }

  return placement;
};

const normalizeInlineAnchorPlacement = (placement: Placement) => {
  return {
    ...placement,
    targetTop: Math.max(48, placement.targetTop),
  };
};

const computePlacement = (
  v: EditorView,
  container: HTMLElement
): Placement | null => {
  const aiInternal = v.state.field(aiAssistantStateField, false);

  if (aiInternal && aiInternal.aiState) {
    return {
      targetLeft: 0,
      targetTop: 0,
      visible: false,
      title: "",
      tooltipRight: false,
    };
  }

  const range = v.state.selection.main;
  const isEmpty = range.empty;
  const from = Math.min(range.from, range.to);
  const to = Math.max(range.from, range.to);
  const scrollerRect = container.getBoundingClientRect();

  if (!v.dom.isConnected) {
    return {
      targetLeft: 0,
      targetTop: 0,
      visible: false,
      title: "",
      tooltipRight: false,
    };
  }

  // Non-empty selection
  if (!isEmpty) {
    const topLine = v.state.doc.lineAt(from);
    const endPosOnTopLine =
      v.state.doc.lineAt(to).number === topLine.number ? to : topLine.to;
    const rect = v.coordsAtPos(endPosOnTopLine, 1);

    if (!rect) {
      return null;
    }

    return normalizeSelectionAnchorPlacement({
      targetLeft:
        rect.right - scrollerRect.left + container.scrollLeft - WIDGET_WIDTH,
      targetTop:
        rect.top -
        scrollerRect.top +
        container.scrollTop -
        WIDGET_WIDTH -
        WIDGET_MARGIN,
      visible: true,
      title: i18n.t("editor.aiIcon.edit_selection", {
        hotkey: `${isMac ? "⌘" : "Ctrl"} + K`,
      }) as string,
      tooltipRight: true,
    });
  }

  const line = v.state.doc.lineAt(range.head);
  const rect = v.coordsAtPos(line.from, 1);

  if (!rect) {
    return null;
  }

  const cursor = range.head;
  const sliceField = v.state.field(StatementGutterMarkerState, false);
  const slice = sliceField?.slice;
  const insideStatement =
    !!slice && cursor >= slice.pos[0] && cursor <= slice.pos[1];

  return normalizeInlineAnchorPlacement({
    targetLeft:
      container.scrollLeft +
      container.clientWidth -
      WIDGET_WIDTH -
      EDITOR_RIGHT_PADDING,
    targetTop: rect.top - scrollerRect.top + container.scrollTop - 6,
    visible: true,
    title: (insideStatement
      ? i18n.t("editor.aiIcon.modify", {
          hotkey: `${isMac ? "⌘" : "Ctrl"} + K`,
        })
      : i18n.t("editor.aiIcon.generate", {
          hotkey: `${isMac ? "⌘" : "Ctrl"} + K`,
        })) as string,
    tooltipRight: false,
  });
};

export const aiIconWidgetExtension = (callbacks: {
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}): Extension =>
  ViewPlugin.define(view => {
    const host = view.scrollDOM;
    const el = createDomElement("div", {
      className: styles.root,
      style: { position: "absolute", display: "none" },
      "data-selection-anchor": "",
      "data-testid": "ai-assistant-trigger",
    });
    const iconContainer = createDomElement("div", { className: styles.icon }, [
      (() => {
        const span = document.createElement("span");
        span.innerHTML = CopilotIcon;
        return span;
      })(),
    ]);
    const tooltipEl = createDomElement("div", {
      className: styles.tooltip,
      role: "tooltip",
      "aria-hidden": "true",
    });
    el.appendChild(iconContainer);
    el.appendChild(tooltipEl);
    host.appendChild(el);

    const handleClick = () => callbacks.onClick();
    const handleEnter = () =>
      callbacks.onMouseEnter && callbacks.onMouseEnter();
    const handleLeave = () =>
      callbacks.onMouseLeave && callbacks.onMouseLeave();
    el.addEventListener("click", handleClick);
    el.addEventListener("mouseenter", handleEnter);
    el.addEventListener("mouseleave", handleLeave);

    const schedulePlace = () => {
      if (!el.isConnected || !host.isConnected || !view.dom.isConnected) {
        return;
      }
      try {
        view.requestMeasure({
          read: () => {
            const placement = computePlacement(view, host);
            if (!placement) {
              return {
                targetLeft: 0,
                targetTop: 0,
                visible: false,
                title: "",
                tooltipRight: false,
              };
            }
            return placement;
          },
          write: ({ targetLeft, targetTop, visible, title, tooltipRight }) => {
            if (!visible) {
              el.style.display = "none";
              return;
            }
            el.style.left = `${targetLeft}px`;
            el.style.top = `${targetTop}px`;
            tooltipEl.textContent = title;
            if (tooltipRight) {
              tooltipEl.classList.add(styles.tooltipRight);
            } else {
              tooltipEl.classList.remove(styles.tooltipRight);
            }
            tooltipEl.setAttribute("aria-hidden", "false");
            el.style.display = "block";
          },
        });
      } catch {
        // Silently ignore measure request errors if view lifecycle is in flux
      }
    };

    // Initial placement
    schedulePlace();

    const onResize = () => schedulePlace();
    window.addEventListener("resize", onResize);
    const onScroll = () => schedulePlace();
    host.addEventListener("scroll", onScroll, { passive: true });

    return {
      update(update: ViewUpdate) {
        const aiChanged = update.transactions.some(tr =>
          tr.effects.some(e => e.is(setAiAssistantState))
        );

        if (
          update.docChanged ||
          update.selectionSet ||
          update.viewportChanged ||
          update.geometryChanged ||
          aiChanged
        ) {
          schedulePlace();
        }
      },
      destroy() {
        window.removeEventListener("resize", onResize);
        host.removeEventListener("scroll", onScroll);
        el.removeEventListener("click", handleClick);
        el.removeEventListener("mouseenter", handleEnter);
        el.removeEventListener("mouseleave", handleLeave);
        el.remove();
      },
    };
  });
